import PropTypes from 'prop-types';
import { css } from '@emotion/react';

import { colors, zLayers } from 'styles/theme';
import { tabletUp } from 'styles/utils';

const ribbonCss = (backgroundColor, slantPosition, zIndex) => css`
  position: relative;
  padding: 45px 0;
  background-color: ${colors[backgroundColor]};
  z-index: ${zIndex};
  color: ${backgroundColor === 'purple' && colors.blank};

  ${tabletUp} {
    padding: 25px 0;
  }

  ${slantPosition && slantedEdgesCss(slantPosition)}
`;

const slantedEdgesCss = slantPosition => {
  const slantEdgesBaseCss = css`
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    transform: skewY(-2deg);
    z-index: ${zLayers.behindAll};
    background-color: inherit;
  `;

  const slantCss = {
    // Styling for the top slanted edge.
    top: css`
      &::before {
        ${slantEdgesBaseCss}
        top: 0;
        transform-origin: top left;
      }
    `,
    // Styling for the bottom slanted edge.
    bottom: css`
      &::after {
        ${slantEdgesBaseCss}
        bottom: 0;
        transform-origin: bottom right;
      }
    `,
    // Styling for a fully slanted ribbon from both edges.
    both: css`
      &::before {
        ${slantEdgesBaseCss}
        top: 0;
        transform-origin: top left;
      }

      &::after {
        ${slantEdgesBaseCss}
        bottom: 0;
        transform-origin: bottom right;
      }
    `,
  };

  return slantCss[slantPosition];
};

/**
 * Layout element that creates an edge-to-edge ribbon with a colored background.
 *
 * @param {object} props
 */
function Ribbon({
  children,
  className = '',
  color = 'pale',
  slant = '',
  zIndex = null,
}) {
  return (
    <div css={ribbonCss(color, slant, zIndex)} className={className}>
      {children}
    </div>
  );
}

Ribbon.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  /** Ribbon's background-color. */
  color: PropTypes.oneOf(['offWhite', 'pale', 'purple']),
  /**
   * Whether the ribbon should be slanted, or horizontal.
   * Can either be fully slanted or just from the bottom/top.
   */
  slant: PropTypes.oneOf(['bottom', 'both', 'top']),
  /** Add a z-index to the Ribbon. Useful when stacking a few ribbons. */
  zIndex: PropTypes.number,
};

export default Ribbon;
